'use client';

/**
 * AnimatedModal Component
 *
 * A modal dialog with smooth entrance/exit animations and glassmorphism.
 */

import Dialog, { type DialogProps } from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogTitle from '@mui/material/DialogTitle';
import Fade from '@mui/material/Fade';
import Grow from '@mui/material/Grow';
import IconButton from '@mui/material/IconButton';
import Slide from '@mui/material/Slide';
import { styled, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { TransitionProps } from '@mui/material/transitions';
import { AnimatePresence, motion } from 'framer-motion';
import type React from 'react';
import { forwardRef } from 'react';
import { glassEffects, glowEffects } from '../../theme/shadows';
import { motionPresets } from '../../theme/transitions';

// ============================================================================
// Types
// ============================================================================

export interface AnimatedModalProps extends Omit<DialogProps, 'TransitionComponent' | 'title'> {
  /**
   * Modal title
   */
  title?: React.ReactNode;

  /**
   * Modal subtitle
   */
  subtitle?: React.ReactNode;

  /**
   * Animation type
   * @default 'scale'
   */
  animation?: 'scale' | 'fade' | 'slide' | 'none';

  /**
   * Slide direction (only for slide animation)
   * @default 'up'
   */
  slideDirection?: 'up' | 'down' | 'left' | 'right';

  /**
   * Enable glow effect around modal
   * @default true
   */
  glow?: boolean;

  /**
   * Show close button
   * @default true
   */
  showCloseButton?: boolean;

  /**
   * Close button click handler
   */
  onClose?: () => void;

  /**
   * Header actions (rendered next to close button)
   */
  headerActions?: React.ReactNode;

  /**
   * Footer actions
   */
  actions?: React.ReactNode;

  /**
   * Children content
   */
  children?: React.ReactNode;
}

// ============================================================================
// Transitions
// ============================================================================

const SlideTransition = forwardRef(function SlideTransition(
  props: TransitionProps & {
    children: React.ReactElement;
    direction: 'up' | 'down' | 'left' | 'right';
  },
  ref: React.Ref<unknown>,
) {
  const { direction, ...rest } = props;
  return <Slide direction={direction} ref={ref} {...rest} />;
});

const GrowTransition = forwardRef(function GrowTransition(
  props: TransitionProps & { children: React.ReactElement },
  ref: React.Ref<unknown>,
) {
  return <Grow ref={ref} {...props} style={{ transformOrigin: 'center' }} />;
});

// ============================================================================
// Styled Components
// ============================================================================

interface StyledDialogProps {
  $isDark: boolean;
  $glow: boolean;
}

const StyledDialog = styled(Dialog, {
  shouldForwardProp: (prop) => !prop.toString().startsWith('$'),
})<StyledDialogProps>(({ theme, $isDark, $glow }) => {
  const glass = $isDark ? glassEffects.dark : glassEffects.light;

  return {
    '& .MuiDialog-paper': {
      borderRadius: 20,
      background: $isDark ? 'rgba(13, 33, 55, 0.95)' : 'rgba(255, 255, 255, 0.95)',
      backdropFilter: glass.backdropBlur,
      border: `1px solid ${glass.border}`,
      boxShadow: $glow ? `${theme.shadows[24]}, ${glowEffects.mixed.medium}` : theme.shadows[24],
      overflow: 'hidden',
    },

    '& .MuiBackdrop-root': {
      backgroundColor: $isDark ? 'rgba(0, 0, 0, 0.7)' : 'rgba(0, 0, 0, 0.5)',
      backdropFilter: 'blur(8px)',
    },
  };
});

const StyledHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'flex-start',
  justifyContent: 'space-between',
  padding: theme.spacing(3, 3, 0, 3),
}));

const HeaderContent = styled('div')({
  flex: 1,
});

const HeaderActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
  marginLeft: theme.spacing(2),
}));

const CloseButton = styled(IconButton)(({ theme }) => ({
  width: 36,
  height: 36,
  borderRadius: 10,
  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.08)' : 'rgba(0, 0, 0, 0.04)',
  transition: 'all 150ms ease',

  '&:hover': {
    backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255, 255, 255, 0.12)' : 'rgba(0, 0, 0, 0.08)',
  },

  '& svg': {
    width: 18,
    height: 18,
  },
}));

const StyledDialogContent = styled(DialogContent)(({ theme }) => ({
  padding: theme.spacing(2, 3, 3, 3),
}));

const StyledDialogActions = styled(DialogActions)(({ theme }) => ({
  padding: theme.spacing(0, 3, 3, 3),
  gap: theme.spacing(1.5),

  '& > :not(:first-of-type)': {
    marginLeft: 0,
  },
}));

// Close icon
const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    strokeLinecap="round"
    strokeLinejoin="round"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

// ============================================================================
// Component
// ============================================================================

export const AnimatedModal = forwardRef<HTMLDivElement, AnimatedModalProps>(
  (
    {
      open,
      onClose,
      title,
      subtitle,
      animation = 'scale',
      slideDirection = 'up',
      glow = true,
      showCloseButton = true,
      headerActions,
      actions,
      children,
      maxWidth = 'sm',
      fullWidth = true,
      ...props
    },
    ref,
  ) => {
    const theme = useTheme();
    const isDark = theme.palette.mode === 'dark';

    const handleClose = () => {
      onClose?.();
    };

    // Get transition component
    const getTransitionComponent = () => {
      switch (animation) {
        case 'fade':
          return Fade;
        case 'slide':
          return forwardRef((props: TransitionProps & { children: React.ReactElement }, ref) => (
            <SlideTransition {...props} ref={ref} direction={slideDirection} />
          ));
        case 'scale':
          return GrowTransition;
        case 'none':
        default:
          return undefined;
      }
    };

    const TransitionComponent = getTransitionComponent();

    return (
      <StyledDialog
        ref={ref}
        open={open}
        onClose={handleClose}
        $isDark={isDark}
        $glow={glow}
        maxWidth={maxWidth}
        fullWidth={fullWidth}
        slots={{ transition: TransitionComponent }}
        {...props}
      >
        <AnimatePresence>
          {open && (
            <motion.div
              initial={motionPresets.fadeIn.initial}
              animate={motionPresets.fadeIn.animate}
              exit={motionPresets.fadeIn.exit}
            >
              {(title || showCloseButton || headerActions) && (
                <StyledHeader>
                  <HeaderContent>
                    {title && (
                      <DialogTitle sx={{ p: 0, mb: subtitle ? 0.5 : 0 }}>
                        {typeof title === 'string' ? (
                          <Typography
                            variant="h5"
                            component="span"
                            sx={{
                              fontWeight: 600,
                            }}
                          >
                            {title}
                          </Typography>
                        ) : (
                          title
                        )}
                      </DialogTitle>
                    )}
                    {subtitle && (
                      <Typography
                        variant="body2"
                        sx={{
                          color: 'text.secondary',
                          mt: 0.5,
                        }}
                      >
                        {subtitle}
                      </Typography>
                    )}
                  </HeaderContent>

                  <HeaderActions>
                    {headerActions}
                    {showCloseButton && (
                      <CloseButton onClick={handleClose} aria-label="Close">
                        <CloseIcon />
                      </CloseButton>
                    )}
                  </HeaderActions>
                </StyledHeader>
              )}

              <StyledDialogContent>{children}</StyledDialogContent>
              {actions && <StyledDialogActions>{actions}</StyledDialogActions>}
            </motion.div>
          )}
        </AnimatePresence>
      </StyledDialog>
    );
  },
);

AnimatedModal.displayName = 'AnimatedModal';

export default AnimatedModal;
